import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Send, Plus, Trash2, Search, Mail, ExternalLink, Loader2, X, Check, AlertCircle, User, FileText, Save, Pencil, Bell, Eye, MousePointerClick, Video, Calendar, Upload, MessageSquare, Smartphone, FileSpreadsheet } from "lucide-react";
import BulkOutreachModal from "./BulkOutreachModal";
import OrgLookupButton from "./OrgLookupButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: "bg-[#1E3050]/40 text-[#857973] border border-[#1E3050]",
  sending: "bg-yellow-500/10 text-yellow-400",
  sent: "bg-green-500/10 text-green-400",
  sent_email: "bg-green-500/10 text-green-400 border border-green-500/30",
  sent_email_sms: "bg-green-500/15 text-green-300 border border-green-500/40",
  waiting: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30",
  meeting_booked: "bg-blue-500/15 text-blue-400 border border-blue-500/40",
  customer: "bg-[#F59073]/15 text-[#F59073] border border-[#F59073]/40",
  not_interested: "bg-red-500/10 text-red-400/80 border border-red-500/20",
  failed: "bg-red-500/10 text-red-400",
};
const STATUS_LABELS = {
  draft: "Utkast",
  sending: "Skickar…",
  sent: "Skickat",
  sent_email: "Mail skickat",
  sent_email_sms: "Mail + SMS",
  waiting: "Vill vänta",
  meeting_booked: "Möte bokat",
  customer: "Kund",
  not_interested: "Inte intresserad",
  failed: "Misslyckades",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "precis nu";
  if (diff < 3600) return `${Math.floor(diff / 60)} min sen`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h sen`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} d sen`;
  return d.toLocaleDateString("sv-SE");
}

export default function OutreachPage() {
  const [messages, setMessages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [statusModal, setStatusModal] = useState(null); // {msg, status}
  const [sending, setSending] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [editingTplId, setEditingTplId] = useState(null);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [savedRefs, setSavedRefs] = useState([]);
  const [showRefPicker, setShowRefPicker] = useState(false);
  const blankForm = {
    to_email: "", to_name: "", subject: "", body: "", preview_url: "",
    price_setup: 5000, price_monthly: 390, currency: "SEK", lead_id: "",
    company_name: "", org_number: "", country: "SE", vat_number: "",
    include_offer: true, agreement_text: "",
    photo_package_offer: true, photo_price_stockholm: 5000, photo_price_outside: 8000, photo_description: "",
    admin_panel_offer: false, admin_panel_price: 6500, admin_panel_description: "",
    crm_offer: false, crm_price: 10000, crm_description: "",
    custom_extras: [],
    references: [],
    video_url: "", video_filename: "",
    calendly_url: "",
    intro_text: "",
    send_sms: false, phone: "", sms_message: "",
  };
  const [form, setForm] = useState(blankForm);
  const [videoUploading, setVideoUploading] = useState(false);

  const fetchData = useCallback(async () => {
    const [msgRes, leadRes, tplRes, refRes] = await Promise.all([
      axios.get(`${API}/admin/outreach`, { withCredentials: true }),
      axios.get(`${API}/admin/leads`, { withCredentials: true }),
      axios.get(`${API}/admin/outreach-templates`, { withCredentials: true }),
      axios.get(`${API}/admin/outreach-references`, { withCredentials: true }),
    ]);
    setMessages(msgRes.data);
    setLeads(leadRes.data);
    setTemplates(tplRes.data);
    setSavedRefs(refRes.data);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm(blankForm);
    setSelectedLead(null);
    setEditingMsgId(null);
  };

  const editMessage = (msg) => {
    setEditingMsgId(msg.id);
    setForm({
      ...blankForm,
      to_email: msg.to_email || "", to_name: msg.to_name || "",
      subject: msg.subject || "", body: msg.body || "", preview_url: msg.preview_url || "",
      price_setup: msg.price_setup ?? 5000, price_monthly: msg.price_monthly ?? 390,
      currency: msg.currency || "SEK", lead_id: msg.lead_id || "",
      company_name: msg.company_name || "", org_number: msg.org_number || "",
      country: msg.country || "SE", vat_number: msg.vat_number || "",
      include_offer: msg.include_offer ?? true,
      agreement_text: msg.agreement_text || "",
      photo_package_offer: msg.photo_package_offer ?? true,
      photo_price_stockholm: msg.photo_price_stockholm ?? 5000,
      photo_price_outside: msg.photo_price_outside ?? 8000,
      photo_description: msg.photo_description || "",
      admin_panel_offer: msg.admin_panel_offer ?? false,
      admin_panel_price: msg.admin_panel_price ?? 6500,
      admin_panel_description: msg.admin_panel_description || "",
      crm_offer: msg.crm_offer ?? false,
      crm_price: msg.crm_price ?? 10000,
      crm_description: msg.crm_description || "",
      custom_extras: Array.isArray(msg.custom_extras) ? msg.custom_extras : [],
      references: Array.isArray(msg.references) ? msg.references : [],
      video_url: msg.video_url || "",
      video_filename: msg.video_filename || "",
      calendly_url: msg.calendly_url || "",
      intro_text: msg.intro_text || "",
      send_sms: false,
      phone: msg.phone || "",
      sms_message: "",
    });
    setShowCompose(true);
  };

  const resendOutreach = async (id) => {
    if (!window.confirm("Re-send this outreach to the recipient with current edits?")) return;
    try {
      await axios.post(`${API}/admin/outreach/${id}/resend`, {}, { withCredentials: true });
      alert("Email re-sent successfully");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to resend");
    }
  };

  // ==== Video upload (Dropbox via backend) ====
  const handleVideoUpload = async (file) => {
    if (!file) return;
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/outreach-videos/upload`, fd, { withCredentials: true });
      setForm(p => ({ ...p, video_url: data.video_url, video_filename: data.filename || file.name }));
    } catch (err) {
      alert("Videouppladdning misslyckades: " + (err.response?.data?.detail || err.message));
    }
    setVideoUploading(false);
  };

  // ==== Reference bank helpers ====
  const saveReferenceToBank = async (idx) => {
    const ref = (form.references || [])[idx];
    if (!ref || (!ref.title && !ref.url)) {
      alert("Fyll i titel eller URL innan du sparar.");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/outreach-references`, ref, { withCredentials: true });
      setSavedRefs(prev => [data, ...prev]);
      alert("Referens sparad i banken!");
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte spara referens");
    }
  };

  const addRefsFromBank = (ids) => {
    const toAdd = savedRefs.filter(r => ids.includes(r.id))
      .map(r => ({ title: r.title || "", url: r.url || "", description: r.description || "" }));
    setForm(p => ({ ...p, references: [...(p.references || []), ...toAdd] }));
    setShowRefPicker(false);
  };

  const deleteReferenceFromBank = async (id) => {
    if (!window.confirm("Ta bort denna referens från banken? (Påverkar inte redan skickade mejl)")) return;
    try {
      await axios.delete(`${API}/admin/outreach-references/${id}`, { withCredentials: true });
      setSavedRefs(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  const sendReminder = async (msg) => {
    const customBody = window.prompt(
      `Send a reminder to ${msg.to_email}.\n\nLeave empty for default Swedish follow-up text, or write your own message:`,
      ""
    );
    if (customBody === null) return; // cancel
    try {
      const res = await axios.post(`${API}/admin/outreach/${msg.id}/reminder`, { body: customBody || "" }, { withCredentials: true });
      alert(`Reminder sent. Total reminders: ${res.data.reminder_count}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send reminder");
    }
  };

  const selectLead = (lead) => {
    setSelectedLead(lead);
    setForm(p => ({ ...p, to_email: lead.email || "", to_name: lead.name || lead.company || "", lead_id: lead.id || "" }));
  };

  const updateStatus = async (msgId, status, extra = {}) => {
    try {
      await axios.patch(`${API}/admin/outreach/${msgId}/status`, { status, ...extra }, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte uppdatera status");
    }
  };

  const convertToCustomer = async (msgId) => {
    try {
      const r = await axios.post(`${API}/admin/outreach/${msgId}/convert-to-customer`, {}, { withCredentials: true });
      alert(`Kund "${r.data.customer.company_name}" skapad!`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Kunde inte konvertera till kund"); }
  };

  const convertToProject = async (msgId) => {
    try {
      const r = await axios.post(`${API}/admin/outreach/${msgId}/convert-to-project`, {}, { withCredentials: true });
      alert(`Projekt "${r.data.project.title}" skapat!`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Kunde inte konvertera till projekt"); }
  };

  const sendOutreach = async (e, sendMode = "auto") => {
    if (e?.preventDefault) e.preventDefault();
    if (!form.to_email) return;
    // sendMode: "email" (force email only), "email_sms" (force both, requires phone), "auto" (use form.send_sms)
    let payload = { ...form };
    if (sendMode === "email") payload.send_sms = false;
    if (sendMode === "email_sms") {
      if (!form.phone?.trim()) { alert("Fyll i telefonnummer (E.164) för att skicka SMS."); return; }
      payload.send_sms = true;
    }
    setSending(true);
    try {
      let resp;
      if (editingMsgId) {
        resp = await axios.put(`${API}/admin/outreach/${editingMsgId}`, payload, { withCredentials: true });
      } else {
        resp = await axios.post(`${API}/admin/outreach/send`, payload, { withCredentials: true });
      }
      const sms = resp?.data?.sms;
      if (payload.send_sms && sms && sms.status !== "sent") {
        alert(`Mejlet skickades, men SMS misslyckades:\n\n${sms.error || "Okänt fel"}\n\n(HTTP ${sms.http || "-"})`);
      }
      setShowCompose(false);
      resetForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  const saveDraft = async () => {
    if (!form.to_email) { alert("Fyll i mottagarens e-post först."); return; }
    setSending(true);
    try {
      if (editingMsgId) {
        await axios.put(`${API}/admin/outreach/${editingMsgId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/outreach/save-draft`, form, { withCredentials: true });
      }
      setShowCompose(false);
      resetForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte spara utkast");
    }
    setSending(false);
  };

  const deleteMsg = async (id) => {
    if (!window.confirm("Delete this outreach record?")) return;
    await axios.delete(`${API}/admin/outreach/${id}`, { withCredentials: true });
    fetchData();
  };

  const saveTemplate = async () => {
    const name = (tplName || "").trim();
    if (!name) { alert("Give the template a name first"); return; }
    setSavingTpl(true);
    try {
      if (editingTplId) {
        await axios.put(`${API}/admin/outreach-templates/${editingTplId}`, {
          name, subject: form.subject, body: form.body,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/outreach-templates`, {
          name, subject: form.subject, body: form.body,
        }, { withCredentials: true });
      }
      setTplName("");
      setEditingTplId(null);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save template");
    }
    setSavingTpl(false);
  };

  const applyTemplate = (tpl) => {
    setForm(p => ({
      ...p,
      subject: tpl.subject || p.subject,
      body: tpl.body || p.body,
    }));
    setShowTemplates(false);
  };

  const editTemplate = (tpl) => {
    setEditingTplId(tpl.id);
    setTplName(tpl.name);
    setForm(p => ({ ...p, subject: tpl.subject || "", body: tpl.body || "" }));
    setShowTemplates(false);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await axios.delete(`${API}/admin/outreach-templates/${id}`, { withCredentials: true });
    fetchData();
  };

  const filtered = messages.filter(m => {
    if (!showArchived && m.status === "not_interested") return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return m.to_email?.toLowerCase().includes(q) || m.to_name?.toLowerCase().includes(q) || m.subject?.toLowerCase().includes(q) || m.company_name?.toLowerCase().includes(q);
  });

  const defaultBody = (name) => `Hej ${name || ""},\n\nJag hoppas allt är bra! Jag ville höra av mig angående er digitala närvaro.\n\nVi på WeDoWeDo bygger moderna, snygga hemsidor med fokus på konvertering och användarvänlighet. Jag har redan tagit mig friheten att ta fram en förhandsvisning åt er.\n\nTa gärna en titt och hör av dig om ni vill köra igång!`;

  return (
    <div data-testid="outreach-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Outreach</h1>
          <p className="text-sm text-[#857973]">{messages.length} emails sent</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowBulk(true)} data-testid="bulk-outreach-btn"
            className="flex items-center gap-2 bg-[#1E3050] border border-[#F59073]/30 text-[#F59073] px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#1E3050]/70 transition-all">
            <FileSpreadsheet className="w-4 h-4" /> Bulk import
          </button>
          <button onClick={() => { resetForm(); setShowCompose(true); }} data-testid="compose-outreach-btn"
            className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
            <Plus className="w-4 h-4" /> Compose
          </button>
        </div>
      </div>

      {showBulk && <BulkOutreachModal onClose={() => setShowBulk(false)} onCreated={fetchData} />}

      {statusModal && (
        <StatusModal data={statusModal} onClose={() => setStatusModal(null)}
          onSave={(extra) => { updateStatus(statusModal.msg.id, statusModal.status, extra); setStatusModal(null); }} />
      )}

      {/* Search */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, company, subject..."
            className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-[#857973] whitespace-nowrap cursor-pointer" data-testid="show-archived-toggle">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="accent-[#F59073]" />
          Show archived
        </label>
      </div>

      {/* Sent messages list */}
      <div className="space-y-2">
        {filtered.map(m => (
          <div key={m.id} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4 hover:border-[#1E3050] transition-all" data-testid={`outreach-${m.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm text-white font-medium">{m.to_name}</span>
                  <span className="text-xs text-[#857973]">{m.to_email}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono ${STATUS_COLORS[m.status] || "bg-[#1E3050]/40 text-[#857973]"}`}>{STATUS_LABELS[m.status] || m.status}</span>
                  {m.status === "waiting" && m.follow_up_date && (
                    <span className="text-[10px] text-yellow-400/80" title={m.status_note || ""}>↻ {m.follow_up_date}</span>
                  )}
                  {m.status === "meeting_booked" && m.meeting_date && (
                    <span className="text-[10px] text-blue-400/80" title={m.status_note || ""}>📅 {String(m.meeting_date).replace("T", " ").slice(0, 16)}</span>
                  )}
                  {m.status === "sent" && m.sent_at && (
                    <span className="text-[10px] text-[#857973]" title={m.sent_at}>
                      skickat {timeAgo(m.sent_at)}
                    </span>
                  )}
                  {(m.email_open_count || 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 flex items-center gap-1"
                      title={`Senast öppnat: ${m.email_last_opened_at ? timeAgo(m.email_last_opened_at) : "-"}`}
                      data-testid={`opened-${m.id}`}>
                      <Eye className="w-3 h-3" /> öppnat {m.email_open_count}x
                    </span>
                  )}
                  {(m.order_page_view_count || 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 flex items-center gap-1"
                      title={`Senast besökt: ${m.order_page_last_viewed_at ? timeAgo(m.order_page_last_viewed_at) : "-"}`}
                      data-testid={`visited-${m.id}`}>
                      <MousePointerClick className="w-3 h-3" /> order-sida {m.order_page_view_count}x
                    </span>
                  )}
                  {m.sms_status === "sent" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30 flex items-center gap-1"
                      title={`SMS skickat ${m.sms_sent_at ? timeAgo(m.sms_sent_at) : ""}`}
                      data-testid={`sms-${m.id}`}>
                      <MessageSquare className="w-3 h-3" /> SMS
                    </span>
                  )}
                  {m.sms_status === "failed" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono bg-red-500/10 text-red-400 border border-red-500/30 flex items-center gap-1"
                      title={m.sms_error || "SMS misslyckades"}>
                      <MessageSquare className="w-3 h-3" /> SMS misslyckades
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#857973] truncate">{m.subject}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-[#857973]">
                  <span>{m.created_at?.slice(0, 10)}</span>
                  <span>{m.price_setup?.toLocaleString()} + {m.price_monthly}/mo {m.currency}</span>
                  {m.preview_url && (
                    <a href={m.preview_url} target="_blank" rel="noreferrer" className="text-[#F59073] hover:underline flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <ExternalLink className="w-3 h-3" /> preview
                    </a>
                  )}
                  {m.order_token && (
                    <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`${window.location.origin}/order/${m.order_token}`); alert("Order-länk kopierad!"); }}
                      data-testid={`copy-order-link-${m.id}`}
                      className="text-[#00F0FF] hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> copy order link
                    </button>
                  )}
                  {m.accepted && (
                    <span className="text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> accepted {m.accepted_at?.slice(0, 10)}</span>
                  )}
                  {m.converted_customer_id && (
                    <a href={`/admin/customers`} className="text-[#F59073] hover:underline flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <User className="w-3 h-3" /> kund skapad
                    </a>
                  )}
                  {m.converted_project_id && (
                    <a href={`/admin/projects`} className="text-[#F59073] hover:underline flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <FileText className="w-3 h-3" /> projekt skapat
                    </a>
                  )}
                  {Array.isArray(m.reminders) && m.reminders.length > 0 && (
                    <span className="text-[#D9A872] flex items-center gap-1" title={`Last reminder: ${m.last_reminder_at?.slice(0, 10)}`}>
                      <Bell className="w-3 h-3" /> {m.reminders.length} reminder{m.reminders.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <select value="" onChange={e => {
                    const v = e.target.value;
                    if (!v) return;
                    if (v === "waiting" || v === "meeting_booked") {
                      setStatusModal({ msg: m, status: v });
                    } else if (v === "convert_customer") {
                      if (window.confirm(`Skapa kund från "${m.company_name || m.to_name}"?`)) convertToCustomer(m.id);
                    } else if (v === "convert_project") {
                      if (window.confirm(`Skapa projekt för denna outreach?`)) convertToProject(m.id);
                    } else {
                      updateStatus(m.id, v);
                    }
                    e.target.value = "";
                  }}
                  data-testid={`status-select-${m.id}`}
                  title="Change CRM status"
                  className="bg-[#1E3050] text-[#F59073] border border-[#F59073]/30 px-2 py-1 rounded-sm text-[11px] font-mono cursor-pointer hover:bg-[#1E3050]/70 max-w-[120px]">
                  <option value="">↻ Status</option>
                  <option value="waiting">⏳ Vill vänta</option>
                  <option value="meeting_booked">📅 Möte bokat</option>
                  <option value="customer">🎯 Kund</option>
                  <option value="not_interested">✕ Inte intresserad</option>
                  {m.status === "customer" && !m.converted_customer_id && <option value="convert_customer">+ Skapa kund</option>}
                  {m.status === "customer" && m.converted_customer_id && !m.converted_project_id && <option value="convert_project">+ Skapa projekt</option>}
                </select>
                <button onClick={() => editMessage(m)} data-testid={`edit-outreach-${m.id}`} title="Edit" className="text-[#857973] hover:text-[#F59073] transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                {!m.accepted && (
                  <button onClick={() => sendReminder(m)} data-testid={`reminder-outreach-${m.id}`} title="Send reminder" className="text-[#857973] hover:text-[#D9A872] transition-colors"><Bell className="w-3.5 h-3.5" /></button>
                )}
                <button onClick={() => resendOutreach(m.id)} data-testid={`resend-outreach-${m.id}`} title="Re-send original email" className="text-[#857973] hover:text-[#00F0FF] transition-colors"><Send className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteMsg(m.id)} title="Delete" className="text-[#857973] hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-12 text-center">
            <Mail className="w-8 h-8 text-[#1E3050] mx-auto mb-3" />
            <p className="text-[#857973]">No outreach emails sent yet</p>
          </div>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCompose(false)} />
          <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 z-10" data-testid="compose-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-outfit font-bold text-lg text-white">{editingMsgId ? "Edit Outreach" : "Compose Outreach"}</h2>
              <button onClick={() => setShowCompose(false)} className="text-[#857973] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {/* Quick select from leads */}
            {leads.length > 0 && !selectedLead && (
              <div className="mb-5">
                <span className="font-mono text-[10px] text-[#857973] block mb-2">QUICK SELECT FROM LEADS</span>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {leads.filter(l => l.email).slice(0, 20).map(l => (
                    <button key={l.id} type="button" onClick={() => selectLead(l)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs bg-[#1E3050]/40 border border-[#1E3050] text-[#857973] hover:text-[#F59073] hover:border-[#F59073]/30 transition-colors">
                      <User className="w-3 h-3" /> {l.name || l.company || l.email}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedLead && (
              <div className="mb-4 bg-[#F59073]/5 border border-[#F59073]/20 rounded-sm px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-[#F59073]">Lead: {selectedLead.name || selectedLead.company} ({selectedLead.email})</span>
                <button onClick={() => { setSelectedLead(null); setForm(p => ({ ...p, lead_id: "" })); }} className="text-[#857973] hover:text-white"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}

            <form onSubmit={sendOutreach} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">TO (EMAIL)</label>
                  <input required type="email" value={form.to_email} onChange={e => setForm(p => ({ ...p, to_email: e.target.value }))}
                    placeholder="namn@foretag.se" data-testid="outreach-email"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">NAME</label>
                  <input value={form.to_name} onChange={e => setForm(p => ({ ...p, to_name: e.target.value }))}
                    placeholder="Namn" data-testid="outreach-name"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
              </div>

              {/* === SMS SECTION (via 46elks) === */}
              <div className="bg-[#1E3050]/30 border-2 border-[#F59073]/30 rounded-sm p-4 relative">
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone className="w-4 h-4 text-[#F59073]" />
                  <span className="font-mono text-[10px] text-[#F59073] tracking-[0.15em]">SMS</span>
                  <span className="text-[10px] text-[#857973]">via 46elks</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.send_sms}
                    onChange={e => setForm(p => ({ ...p, send_sms: e.target.checked }))}
                    className="accent-[#F59073]" data-testid="send-sms-checkbox" />
                  <span className="text-xs text-white">Skicka även SMS med preview-länk + bokningslänk</span>
                </label>
                {form.send_sms && (
                  <div className="space-y-2 mt-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-mono text-[#857973] block mb-1">TELEFON *</label>
                        <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                          placeholder="+46707445405"
                          data-testid="sms-phone-input"
                          className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none" />
                      </div>
                      <div className="flex items-end">
                        <p className="text-[10px] text-[#857973]">E.164-format inkl. landskod (+46…). Tomt = SMS skickas inte.</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-mono text-[#857973] block mb-1">CUSTOM SMS-TEXT (valfri — använd tokens {"{{name}} {{preview}} {{boka}}"})</label>
                      <textarea value={form.sms_message} onChange={e => setForm(p => ({ ...p, sms_message: e.target.value }))}
                        rows={2}
                        placeholder={`Lämna tomt för standard:\n"Hej ${(form.to_name || "X").split(" ")[0]}! Jag har lagt upp ett förslag åt er. Förhandsvisning: ... Boka 30-min: ...  /WeDoWeDo"`}
                        data-testid="sms-message-input"
                        className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none resize-none" />
                    </div>
                  </div>
                )}
              </div>

              {/* === EMAIL SECTION === */}
              <div className="border-t border-[#1E3050] pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-4 h-4 text-[#F59073]" />
                  <span className="font-mono text-[10px] text-[#F59073] tracking-[0.15em]">EMAIL</span>
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">SUBJECT</label>
                  <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                    placeholder={`Hemsida för ${form.to_name || "er"} — WeDoWeDo`} data-testid="outreach-subject"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] text-[#857973] mb-1 block">PREVIEW LINK (EMERGENT) <span className="text-[#F59073]">*</span></label>
                <input value={form.preview_url} onChange={e => setForm(p => ({ ...p, preview_url: e.target.value }))}
                  placeholder="https://xxx-preview.preview.emergentagent.com" data-testid="outreach-preview"
                  required type="url"
                  className={`w-full bg-[#0A1628] border text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none ${form.preview_url ? "border-[#1E3050]" : "border-[#F59073]/40"}`} />
                {!form.preview_url && (
                  <p className="text-[10px] text-[#F59073] mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Preview-länk krävs innan du kan skicka.
                  </p>
                )}
              </div>

              {/* References — other work we've done */}
              <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-white">Referenser (länkar till tidigare arbete)</p>
                  <div className="flex items-center gap-3">
                    {savedRefs.length > 0 && (
                      <button type="button" onClick={() => setShowRefPicker(true)}
                        data-testid="pick-saved-refs-btn"
                        className="text-[10px] text-[#D9A872] hover:underline flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Välj från sparade ({savedRefs.length})
                      </button>
                    )}
                    <button type="button" onClick={() => setForm(p => ({ ...p, references: [...(p.references || []), { title: "", url: "", description: "" }] }))}
                      data-testid="add-reference-btn"
                      className="text-[10px] text-[#F59073] hover:underline flex items-center gap-1">+ Lägg till</button>
                  </div>
                </div>
                {(form.references || []).length === 0 && (
                  <p className="text-[10px] text-[#857973]">Lägg till länkar till tidigare hemsidor du gjort, så kunden kan se din kvalitet. Spara dina favoriter i referensbanken så slipper du skriva om dem.</p>
                )}
                {(form.references || []).map((ref, i) => (
                  <div key={i} className="border border-[#1E3050] rounded-sm p-2 mt-2 space-y-2" data-testid={`reference-${i}`}>
                    <div className="flex gap-2">
                      <input value={ref.title} onChange={e => setForm(p => ({ ...p, references: p.references.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x) }))}
                        placeholder="Titel (t.ex. FlowFocus.se)"
                        className="flex-1 bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                      <button type="button" onClick={() => saveReferenceToBank(i)}
                        title="Spara denna referens i banken"
                        data-testid={`save-ref-${i}`}
                        className="text-[#857973] hover:text-[#D9A872] px-1" aria-label="Spara">
                        <Save className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => setForm(p => ({ ...p, references: p.references.filter((_, idx) => idx !== i) }))}
                        className="text-[#857973] hover:text-red-400 px-1">×</button>
                    </div>
                    <input value={ref.url} onChange={e => setForm(p => ({ ...p, references: p.references.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x) }))}
                      placeholder="https://..."
                      className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                    <input value={ref.description} onChange={e => setForm(p => ({ ...p, references: p.references.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x) }))}
                      placeholder="Kort beskrivning (valfri, t.ex. 'ADHD task management app')"
                      className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                  </div>
                ))}
              </div>

              {/* Personal video */}
              <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Video className="w-3.5 h-3.5 text-[#F59073]" />
                  <p className="text-xs text-white">Personlig video (visas högst upp på bokningssidan)</p>
                </div>
                {form.video_url ? (
                  <div className="space-y-2">
                    <video src={form.video_url} controls className="w-full max-h-48 bg-black rounded-sm" data-testid="compose-video-preview" />
                    <div className="flex items-center justify-between text-[10px] text-[#857973]">
                      <span className="truncate">{form.video_filename || "Uppladdad"}</span>
                      <button type="button" onClick={() => setForm(p => ({ ...p, video_url: "", video_filename: "" }))}
                        className="text-red-400 hover:underline" data-testid="remove-video-btn">Ta bort</button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className={`flex items-center justify-center gap-2 border border-dashed rounded-sm py-3 text-xs cursor-pointer transition-colors ${videoUploading ? "border-[#857973] text-[#857973]" : "border-[#1E3050] text-[#D9A872] hover:border-[#F59073]"}`} data-testid="upload-video-label">
                      {videoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {videoUploading ? "Laddar upp till Dropbox..." : "Ladda upp video (max 80 MB)"}
                      <input type="file" accept="video/*" disabled={videoUploading} className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleVideoUpload(f); }} />
                    </label>
                    <p className="text-[10px] text-[#857973]">Eller klistra in en länk (Loom, YouTube, Vimeo) direkt:</p>
                    <input value={form.video_url} onChange={e => setForm(p => ({ ...p, video_url: e.target.value, video_filename: "" }))}
                      placeholder="https://..."
                      data-testid="video-url-input"
                      className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none" />
                  </div>
                )}
              </div>

              {/* Calendly URL */}
              <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-3.5 h-3.5 text-[#F59073]" />
                  <p className="text-xs text-white">Calendly-länk för bokning</p>
                </div>
                <input value={form.calendly_url} onChange={e => setForm(p => ({ ...p, calendly_url: e.target.value }))}
                  placeholder="https://calendly.com/hey-wedowedo/30min (lämna tomt för standard)"
                  data-testid="calendly-url-input"
                  className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none" />
                <p className="text-[10px] text-[#857973] mt-1">Standard: <span className="font-mono">calendly.com/hey-wedowedo/30min</span></p>
              </div>

              {/* (SMS-block flyttat till toppen, nära e-postfältet) */}

              {/* Toggle: include offer & order link or just plain mail */}
              <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.include_offer}
                    onChange={e => setForm(p => ({ ...p, include_offer: e.target.checked }))}
                    className="mt-0.5 accent-[#F59073]" data-testid="include-offer-toggle" />
                  <div>
                    <p className="text-sm text-white">Inkludera erbjudande & beställningslänk</p>
                    <p className="text-xs text-[#857973]">Av: bara ett vanligt mail. På: priser + "Beställ nu"-knapp + order-sida.</p>
                  </div>
                </label>
              </div>

              {form.include_offer && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">SETUP PRICE</label>
                  <input type="number" value={form.price_setup} onChange={e => setForm(p => ({ ...p, price_setup: Number(e.target.value) }))}
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">MONTHLY PRICE</label>
                  <input type="number" value={form.price_monthly} onChange={e => setForm(p => ({ ...p, price_monthly: Number(e.target.value) }))}
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">CURRENCY</label>
                  <select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    <option value="SEK">SEK</option><option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              )}

              {/* Customer details for order page */}
              {form.include_offer && (
              <details className="border border-[#1E3050] rounded-sm">
                <summary className="cursor-pointer px-3 py-2 text-xs font-mono text-[#857973] hover:text-white">▸ COMPANY & ORDER PAGE DETAILS</summary>
                <div className="p-3 space-y-3 border-t border-[#1E3050]">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="font-mono text-[10px] text-[#857973] mb-1 block">COMPANY NAME</label>
                      <input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
                        placeholder="Acme AB" className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] text-[#857973] mb-1 block">ORG NUMBER</label>
                      <div className="flex items-center gap-2">
                        <input value={form.org_number} onChange={e => setForm(p => ({ ...p, org_number: e.target.value }))}
                          placeholder="556012-3456" className="flex-1 bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                        <OrgLookupButton compact orgnr={form.org_number}
                          onResult={d => setForm(p => ({
                            ...p,
                            company_name: p.company_name || d.company_name,
                            org_number: d.orgnr || p.org_number,
                            vat_number: p.vat_number || d.vat_number,
                            country: d.country || p.country,
                          }))} />
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] text-[#857973] mb-1 block">COUNTRY</label>
                      <select value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))}
                        className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                        <option value="SE">SE</option><option value="NO">NO</option><option value="DK">DK</option><option value="FI">FI</option><option value="EE">EE</option><option value="DE">DE</option><option value="GB">GB</option>
                      </select>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] text-[#857973] mb-1 block">VAT NUMBER</label>
                      <input value={form.vat_number} onChange={e => setForm(p => ({ ...p, vat_number: e.target.value }))}
                        placeholder="EE..." className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                    </div>
                  </div>

                  <div>
                    <label className="font-mono text-[10px] text-[#857973] mb-1 block">AGREEMENT TEXT (shown on order page)</label>
                    <textarea value={form.agreement_text} onChange={e => setForm(p => ({ ...p, agreement_text: e.target.value }))}
                      rows={5} placeholder="Lämna tomt för standardtext (inkl. dina företagsuppgifter + kundens uppgifter)..."
                      className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-xs focus:border-[#D9A872] outline-none resize-none font-mono leading-relaxed" />
                  </div>

                  {/* Add-on offers */}
                  <div className="space-y-3 pt-3 border-t border-[#1E3050]">
                    <p className="font-mono text-[10px] text-[#857973]">EXTRA TILLVAL PÅ ORDER-SIDAN</p>

                    {/* Photo */}
                    <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                      <label className="flex items-start gap-2 cursor-pointer mb-2">
                        <input type="checkbox" checked={form.photo_package_offer}
                          onChange={e => setForm(p => ({ ...p, photo_package_offer: e.target.checked }))}
                          className="mt-0.5 accent-[#F59073]" data-testid="photo-offer-toggle" />
                        <span className="text-xs text-white">Fotopaket</span>
                      </label>
                      {form.photo_package_offer && (
                        <div className="pl-5 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="font-mono text-[9px] text-[#857973] block mb-0.5">PRIS STOCKHOLM</label>
                              <input type="number" value={form.photo_price_stockholm} onChange={e => setForm(p => ({ ...p, photo_price_stockholm: Number(e.target.value) }))}
                                className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                            </div>
                            <div>
                              <label className="font-mono text-[9px] text-[#857973] block mb-0.5">PRIS UTANFÖR</label>
                              <input type="number" value={form.photo_price_outside} onChange={e => setForm(p => ({ ...p, photo_price_outside: Number(e.target.value) }))}
                                className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                            </div>
                          </div>
                          <div>
                            <label className="font-mono text-[9px] text-[#857973] block mb-0.5">EGEN BESKRIVNING (valfri)</label>
                            <textarea rows={2} value={form.photo_description} onChange={e => setForm(p => ({ ...p, photo_description: e.target.value }))}
                              placeholder="T.ex. 'Heldagsfotografering på er restaurang...' Lämna tomt för standardtext."
                              className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none resize-none font-manrope" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Admin panel */}
                    <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                      <label className="flex items-start gap-2 cursor-pointer mb-2">
                        <input type="checkbox" checked={form.admin_panel_offer}
                          onChange={e => setForm(p => ({ ...p, admin_panel_offer: e.target.checked }))}
                          className="mt-0.5 accent-[#F59073]" data-testid="admin-panel-offer-toggle" />
                        <span className="text-xs text-white">Adminpanel</span>
                      </label>
                      {form.admin_panel_offer && (
                        <div className="pl-5 space-y-2">
                          <div>
                            <label className="font-mono text-[9px] text-[#857973] block mb-0.5">PRIS</label>
                            <input type="number" value={form.admin_panel_price} onChange={e => setForm(p => ({ ...p, admin_panel_price: Number(e.target.value) }))}
                              className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                          </div>
                          <div>
                            <label className="font-mono text-[9px] text-[#857973] block mb-0.5">EGEN BESKRIVNING (valfri)</label>
                            <textarea rows={2} value={form.admin_panel_description} onChange={e => setForm(p => ({ ...p, admin_panel_description: e.target.value }))}
                              placeholder="T.ex. 'Skräddarsydd panel för bokningshantering med statistik på...'"
                              className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none resize-none font-manrope" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* CRM */}
                    <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                      <label className="flex items-start gap-2 cursor-pointer mb-2">
                        <input type="checkbox" checked={form.crm_offer}
                          onChange={e => setForm(p => ({ ...p, crm_offer: e.target.checked }))}
                          className="mt-0.5 accent-[#F59073]" data-testid="crm-offer-toggle" />
                        <span className="text-xs text-white">CRM-system</span>
                      </label>
                      {form.crm_offer && (
                        <div className="pl-5 space-y-2">
                          <div>
                            <label className="font-mono text-[9px] text-[#857973] block mb-0.5">PRIS</label>
                            <input type="number" value={form.crm_price} onChange={e => setForm(p => ({ ...p, crm_price: Number(e.target.value) }))}
                              className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                          </div>
                          <div>
                            <label className="font-mono text-[9px] text-[#857973] block mb-0.5">EGEN BESKRIVNING (valfri)</label>
                            <textarea rows={2} value={form.crm_description} onChange={e => setForm(p => ({ ...p, crm_description: e.target.value }))}
                              placeholder="T.ex. 'CRM med integration mot Fortnox och er nuvarande bokningssida...'"
                              className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none resize-none font-manrope" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Custom extras */}
                    <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-white">Egna tillval</p>
                        <button type="button" onClick={() => setForm(p => ({ ...p, custom_extras: [...(p.custom_extras || []), { name: "", price: 0, description: "" }] }))}
                          data-testid="add-custom-extra-btn"
                          className="text-[10px] text-[#F59073] hover:underline flex items-center gap-1">+ Lägg till</button>
                      </div>
                      {(form.custom_extras || []).length === 0 && (
                        <p className="text-[10px] text-[#857973]">Lägg till skräddarsydda tillval (t.ex. SEO-paket, animationer, integrationer).</p>
                      )}
                      {(form.custom_extras || []).map((ex, i) => (
                        <div key={i} className="border border-[#1E3050] rounded-sm p-2 mt-2 space-y-2" data-testid={`custom-extra-${i}`}>
                          <div className="flex gap-2">
                            <input value={ex.name} onChange={e => setForm(p => ({ ...p, custom_extras: p.custom_extras.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) }))}
                              placeholder="Tillvalsnamn (t.ex. SEO-paket)" data-testid={`custom-extra-name-${i}`}
                              className="flex-1 bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                            <input type="number" value={ex.price} onChange={e => setForm(p => ({ ...p, custom_extras: p.custom_extras.map((x, idx) => idx === i ? { ...x, price: Number(e.target.value) } : x) }))}
                              placeholder="Pris" data-testid={`custom-extra-price-${i}`}
                              className="w-24 bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                            <button type="button" onClick={() => setForm(p => ({ ...p, custom_extras: p.custom_extras.filter((_, idx) => idx !== i) }))}
                              className="text-[#857973] hover:text-red-400 px-1">×</button>
                          </div>
                          <textarea rows={2} value={ex.description} onChange={e => setForm(p => ({ ...p, custom_extras: p.custom_extras.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x) }))}
                            placeholder="Beskrivning som visas på order-sidan (valfri)"
                            className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none resize-none font-manrope" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </details>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-mono text-[10px] text-[#857973]">MESSAGE</label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setShowTemplates(t => !t)} data-testid="show-templates-btn"
                      className="text-[10px] text-[#F59073] hover:underline flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Templates ({templates.length})
                    </button>
                    {!form.body && (
                      <button type="button" onClick={() => setForm(p => ({ ...p, body: defaultBody(p.to_name) }))}
                        className="text-[10px] text-[#F59073] hover:underline">Use default</button>
                    )}
                  </div>
                </div>

                {showTemplates && (
                  <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3 mb-2 max-h-56 overflow-y-auto" data-testid="templates-list">
                    {templates.length === 0 ? (
                      <p className="text-xs text-[#857973] py-2">No templates yet. Write a message below, name it and click "Save as template".</p>
                    ) : (
                      <div className="space-y-1">
                        {templates.map(t => (
                          <div key={t.id} className="flex items-center gap-2 hover:bg-[#1E3050]/30 rounded-sm px-2 py-1.5 group">
                            <button type="button" onClick={() => applyTemplate(t)} data-testid={`apply-template-${t.id}`} className="flex-1 text-left">
                              <p className="text-sm text-white">{t.name}</p>
                              <p className="text-[10px] text-[#857973] truncate">{t.subject || t.body?.slice(0, 60)}</p>
                            </button>
                            <button type="button" onClick={() => editTemplate(t)} className="opacity-0 group-hover:opacity-100 text-[#857973] hover:text-[#F59073] transition-all" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                            <button type="button" onClick={() => deleteTemplate(t.id)} className="opacity-0 group-hover:opacity-100 text-[#857973] hover:text-red-400 transition-all" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3">
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">INTRO-TEXT (visas högst upp på bokningssidan)</label>
                  <textarea value={form.intro_text} onChange={e => setForm(p => ({ ...p, intro_text: e.target.value }))}
                    placeholder="T.ex. 'Inspirerat av er nuvarande hemsida har jag fokuserat på att lyfta er produkt och göra första intrycket starkare. Bläddra ner så ser du upplägget.'"
                    rows={3} data-testid="outreach-intro-text"
                    className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-xs outline-none resize-none" />
                </div>

                <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                  placeholder="Write your personal message here... (leave empty for default template)"
                  rows={8} data-testid="outreach-body"
                  className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-3 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none font-manrope leading-relaxed" />

                {/* Save current as template */}
                <div className="flex items-center gap-2 mt-2">
                  <input value={tplName} onChange={e => setTplName(e.target.value)} placeholder={editingTplId ? "Update template name..." : "Template name (e.g. 'First touch SaaS')"}
                    data-testid="template-name-input"
                    className="flex-1 bg-[#0A1628] border border-[#1E3050] text-white px-3 py-1.5 rounded-sm text-xs focus:border-[#D9A872] outline-none" />
                  <button type="button" onClick={saveTemplate} disabled={savingTpl || !tplName.trim() || (!form.subject && !form.body)} data-testid="save-template-btn"
                    className="flex items-center gap-1 bg-[#1E3050] hover:bg-[#1E3050]/80 text-white text-xs px-3 py-1.5 rounded-sm disabled:opacity-50">
                    {savingTpl ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    {editingTplId ? "Update template" : "Save as template"}
                  </button>
                  {editingTplId && (
                    <button type="button" onClick={() => { setEditingTplId(null); setTplName(""); }} className="text-[#857973] hover:text-white text-xs">Cancel</button>
                  )}
                </div>
              </div>

              {/* Preview */}
              <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-4">
                <span className="font-mono text-[10px] text-[#857973] block mb-2">EMAIL PREVIEW</span>
                <div className="text-xs text-[#857973] space-y-1">
                  <p>To: <span className="text-white">{form.to_name} &lt;{form.to_email}&gt;</span></p>
                  <p>Subject: <span className="text-white">{form.subject || `Hemsida för ${form.to_name || "er"} — WeDoWeDo`}</span></p>
                  <p>Pricing: <span className="text-[#F59073]">{form.price_setup?.toLocaleString()} {form.currency} setup + {form.price_monthly} {form.currency}/mo</span></p>
                  {form.preview_url && <p>Preview: <span className="text-[#F59073]">{form.preview_url}</span></p>}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button type="button" onClick={saveDraft} disabled={sending || !form.to_email} data-testid="save-draft-btn"
                  title="Spara utan att skicka — du kan skicka senare"
                  className="flex items-center justify-center gap-2 bg-[#1E3050] text-white py-3 px-5 rounded-sm text-sm font-medium hover:bg-[#2A4060] transition-all disabled:opacity-50">
                  <Save className="w-4 h-4" /> Spara
                </button>
                {!editingMsgId && (
                  <>
                    <button type="button" onClick={(e) => sendOutreach(e, "email")}
                      disabled={sending || !form.to_email || !form.preview_url} data-testid="send-email-only-btn"
                      title={!form.preview_url ? "Lägg till preview-länk först" : "Skicka bara mail"}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#1E3050] border border-[#F59073]/40 text-white py-3 rounded-sm text-sm font-medium hover:bg-[#2A4060] transition-all disabled:opacity-50">
                      <Mail className="w-4 h-4" /> Skicka mail
                    </button>
                    <button type="button" onClick={(e) => sendOutreach(e, "email_sms")}
                      disabled={sending || !form.to_email || !form.preview_url || !form.phone} data-testid="send-email-sms-btn"
                      title={!form.phone ? "Telefonnummer krävs för SMS" : !form.preview_url ? "Lägg till preview-länk först" : "Skicka mail + SMS"}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#F59073] text-black py-3 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /><Smartphone className="w-3.5 h-3.5" /></>}
                      {sending ? "Skickar..." : "Skicka mail + SMS"}
                    </button>
                  </>
                )}
                {editingMsgId && (
                  <button type="submit" disabled={sending || !form.to_email} data-testid="save-changes-btn"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#F59073] text-black py-3 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {sending ? "Sparar..." : "Spara ändringar"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reference Bank Picker */}
      {showRefPicker && <ReferenceBankPicker
        savedRefs={savedRefs}
        onClose={() => setShowRefPicker(false)}
        onAdd={addRefsFromBank}
        onDelete={deleteReferenceFromBank}
      />}
    </div>
  );
}

function ReferenceBankPicker({ savedRefs, onClose, onAdd, onDelete }) {
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const filtered = savedRefs.filter(r => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.title || "").toLowerCase().includes(s) || (r.url || "").toLowerCase().includes(s) || (r.description || "").toLowerCase().includes(s);
  });
  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-2xl p-6 z-10 max-h-[85vh] overflow-y-auto" data-testid="ref-picker-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit font-medium text-white text-lg">Referensbank</h3>
          <button onClick={onClose} className="text-[#857973] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Sök bland sparade referenser..."
            className="w-full bg-[#0A1628] border border-[#1E3050] text-white pl-10 pr-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
        </div>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {filtered.length === 0 && (
            <p className="text-xs text-[#857973] text-center py-8">Inga sparade referenser ännu. Lägg till en referens i ett mejl och klicka på spara-ikonen.</p>
          )}
          {filtered.map(r => (
            <label key={r.id} className={`flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors ${picked.includes(r.id) ? "border-[#F59073] bg-[#F59073]/5" : "border-[#1E3050] hover:border-[#D9A872]"}`}
              data-testid={`ref-bank-${r.id}`}>
              <input type="checkbox" checked={picked.includes(r.id)} onChange={() => toggle(r.id)}
                className="mt-1 accent-[#F59073]" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{r.title || r.url}</p>
                {r.url && <p className="text-[10px] text-[#D9A872] truncate font-mono">{r.url}</p>}
                {r.description && <p className="text-xs text-[#857973] mt-0.5 line-clamp-2">{r.description}</p>}
              </div>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(r.id); }}
                className="text-[#857973] hover:text-red-400 p-1" title="Ta bort från banken">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </label>
          ))}
        </div>
        <div className="flex gap-3 mt-5 pt-4 border-t border-[#1E3050]">
          <button type="button" onClick={onClose}
            className="flex-1 bg-[#1E3050] text-white py-2.5 rounded-sm text-sm font-medium hover:bg-[#1E3050]/70">
            Avbryt
          </button>
          <button type="button" onClick={() => onAdd(picked)} disabled={picked.length === 0}
            data-testid="add-picked-refs-btn"
            className="flex-1 bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] disabled:opacity-50">
            Lägg till {picked.length > 0 && `(${picked.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusModal({ data, onSave, onClose }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const isWaiting = data.status === "waiting";
  const label = isWaiting ? "Vill vänta" : "Möte bokat";
  const dateLabel = isWaiting ? "FOLLOW-UP DATUM" : "MÖTESDATUM";
  const dateField = isWaiting ? "follow_up_date" : "meeting_date";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="status-modal">
      <div onClick={e => e.stopPropagation()} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit text-lg text-white">Sätt status: {label}</h3>
          <button onClick={onClose} className="text-[#857973] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">{dateLabel}</label>
            <input type={isWaiting ? "date" : "datetime-local"} value={date} onChange={e => setDate(e.target.value)}
              data-testid="status-date" autoFocus
              className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">ANTECKNING (valfri)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              placeholder={isWaiting ? "T.ex. 'Vill prata efter semestern'" : "T.ex. 'Demo-möte med VD och CTO'"}
              className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none resize-none" />
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 bg-[#1E3050] text-white py-2 rounded-sm text-sm">Avbryt</button>
            <button onClick={() => onSave({ [dateField]: date, note })} disabled={!date} data-testid="status-save"
              className="flex-1 bg-[#F59073] text-black py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] disabled:opacity-50">
              Spara
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
